/**
 * MobileTasksView - Today's Tasks view for mobile (admin only)
 * 
 * Uses preloaded globalTasks from App.js for instant tab switching.
 * - Today's tasks with add/edit/delete/complete functionality
 * - AI suggested tasks (scrollable below)
 * - Calendar events
 * 
 * Data syncs with the same Supabase tables as the desktop Planner.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, writeTimeoutSignal } from '../../../lib/supabase';
import MentionSuggestions from '@/components/shared/MentionSuggestions';
import SharedTasksSection from '@/components/shared/SharedTasksSection';
import SharedTaskEditDialog from '@/components/shared/SharedTaskEditDialog';
import { SubtaskProgressChip, SubtaskChecklist } from '@/components/shared/Subtasks';
import PriorityCheckbox, { comparePriorityTasks } from '@/components/shared/PriorityCheckbox';
import { usePlannerSubtasks } from '../../planner/hooks/usePlannerSubtasks';
import { useDesign } from '@/contexts/DesignContext';
import { priorityDot } from '@/components/studio/planner/parts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Trash2,
  Repeat,
  Lightbulb,
  ExternalLink,
  Bot,
  Calendar,
  ListChecks,
  CheckCircle2,
  X as XIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { formatTime12h, generateTimeOptions } from '../../../utils/timeFormat';

const TIME_OPTIONS = generateTimeOptions();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default suggested tasks by day of week
const defaultSuggestions = {
  monday: [
    'Connect with Sr. Pastor to review previous service & discuss anything important',
    'Check upcoming teams for cancelations. Fix and reschedule.',
    'Ensure this week\'s set is finalized (keys, songs, leaders)',
    'Send group text to upcoming team(s) with notes about set and some encouragements',
  ],
  tuesday: [
    'Review livestream (if applicable) and share thoughts with production team',
    'Print chord charts for upcoming service',
    'Make tracks for upcoming service',
  ],
  wednesday: [
    'One-on-One Training w/ a developing team member',
    'Text or call a few team members to see how they\'re doing',
    'Pray for team members by name and life-circumstance',
  ],
  thursday: [
    'Meet and pray with direct reports',
    '10 minute check-in meeting with production staff',
    'Lead rehearsal',
  ],
  friday: [],
  saturday: [],
  sunday: [
    "Look ahead at this week's major events and goals",
    'Warm up voice',
    'Pre-service talk-through and prayer with teams',
    'Send Sunday teams "thanks for serving" text',
  ],
};

function MobileTasksView({
  user, globalTasks = [], setGlobalTasks,
  sharedTasks = [], sharedContext = {}, onToggleSharedTask, onDeleteSharedTask,
  onUpdateSharedTask, onCreateSharedTask, highlightSharedTaskId, setHoveredItem,
}) {
  const { isStudio } = useDesign();
  // Use global tasks from App.js (preloaded) - no loading delay!
  const tasks = globalTasks;
  const setTasks = setGlobalTasks || (() => {});
  const [selectedDate] = useState(new Date()); // Always today for mobile
  
  // Task form state
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingSharedTask, setEditingSharedTask] = useState(null);
  const [newTask, setNewTask] = useState({ 
    title: '', description: '', time: '', priority: 'none', assigned_to: null, assigned_to_name: ''
  });
  const isAdmin = !!sharedContext?.is_admin;
  const assignableMembers = isAdmin ? (sharedContext?.members || []) : [];
  const [mentionQuery, setMentionQuery] = useState(null);
  const mentionSuggestionsRef = useRef(null);
  const titleInputRef = useRef(null);

  // Sub-tasks (template per series, checkmarks per instance)
  const subtasksApi = usePlannerSubtasks(user?.id);
  const [expandedSubtasks, setExpandedSubtasks] = useState(() => new Set());
  const toggleSubtaskExpand = (taskId) => {
    setExpandedSubtasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  
  // Edit task state
  const [showEditTask, setShowEditTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Suggestions collapsed state
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(() => {
    const saved = localStorage.getItem('mobileTasksSuggestionsCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  // Google Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);

  // Persist suggestions collapsed state
  useEffect(() => {
    localStorage.setItem('mobileTasksSuggestionsCollapsed', JSON.stringify(suggestionsCollapsed));
  }, [suggestionsCollapsed]);

  // Fetch calendar events for today
  const fetchCalendarEvents = useCallback(async () => {
    try {
      // P2-h (DIV-032): the backend only exposes POST /calendar/google/events
      // with a time range (same call PlannerApp makes); the GET-by-month form
      // never existed and failed silently.
      const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0, 23, 59, 59, 999);
      const response = await fetch(`${API_URL}/api/calendar/google/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, time_min: monthStart.toISOString(), time_max: monthEnd.toISOString() })
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarEvents(data.events || []);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    }
  }, [selectedDate, user.id]);

  // Check Google Calendar status
  const checkCalendarStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/google/status/${user.id}`);
      const data = await response.json();
      setGoogleCalendarConnected(data.connected);
      if (data.connected && data.visible_calendars?.length > 0) {
        fetchCalendarEvents();
      }
    } catch (err) {
      console.error('Error checking calendar status:', err);
    }
  }, [user.id, fetchCalendarEvents]);

  // Initial calendar status check (tasks are already loaded via globalTasks)
  useEffect(() => {
    checkCalendarStatus();
  }, [checkCalendarStatus]);

  // Get tasks for today (including overdue)
  const getTasksForToday = () => {
    const todayStr = format(selectedDate, 'yyyy-MM-dd');
    const todayTasks = tasks.filter(t => t.date === todayStr);
    
    // Include overdue incomplete tasks
    const overdueTasks = tasks.filter(t => 
      t.date < todayStr && !t.completed
    ).map(t => ({ ...t, isOverdue: true }));
    
    // Completed at bottom; among incomplete: overdue → priority → time
    return [...overdueTasks, ...todayTasks].sort((a, b) => {
      if (!!a.completed !== !!b.completed) return a.completed ? 1 : -1;
      return comparePriorityTasks(a, b);
    });
  };

  // Get events for today
  const getEventsForToday = () => {
    const todayStr = format(selectedDate, 'yyyy-MM-dd');
    return calendarEvents.filter(e => {
      const eventDate = format(new Date(e.start), 'yyyy-MM-dd');
      return eventDate === todayStr;
    });
  };

  // Get suggestions for today
  const getSuggestionsForToday = () => {
    const dayName = format(selectedDate, 'EEEE').toLowerCase();
    return defaultSuggestions[dayName] || [];
  };

  // Add task
  const addTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    // Shared/assigned task → church-level API (one synced record)
    if (newTask.assigned_to && onCreateSharedTask) {
      try {
        await onCreateSharedTask({
          item_type: 'task',
          title: newTask.title.trim(),
          description: newTask.description.trim() || '',
          assigned_to: newTask.assigned_to,
          due_date: format(selectedDate, 'yyyy-MM-dd'),
          priority_level: newTask.priority || 'none',
        });
        toast.success(`Task assigned to ${newTask.assigned_to_name || 'teammate'}`);
        setNewTask({ title: '', description: '', time: '', priority: 'none', assigned_to: null, assigned_to_name: '' });
        setShowAddTask(false);
      } catch (err) {
        console.error('Error assigning task:', err);
        toast.error(err.message || 'Failed to assign task');
      }
      return;
    }

    try {
      const taskData = {
        user_id: user.id,
        title: newTask.title.trim(),
        description: newTask.description.trim() || null,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: newTask.time || null,
        priority: newTask.priority,
        completed: false
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert(taskData)
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [...prev, data]);
      setNewTask({ title: '', description: '', time: '', priority: 'none', assigned_to: null, assigned_to_name: '' });
      setShowAddTask(false);
      toast.success('Task added!');
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Failed to add task');
    }
  };

  // Toggle task complete
  const toggleTaskComplete = async (task) => {
    // Optimistic flip with revert — never block the checkbox on the network.
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, completed: !task.completed } : t
    ));
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ completed: !task.completed })
        .eq('id', task.id)
        .abortSignal(writeTimeoutSignal());

      if (error) throw error;
    } catch (err) {
      console.error('Error toggling task:', err);
      setTasks(prev => prev.map(t =>
        t.id === task.id ? { ...t, completed: task.completed } : t
      ));
      toast.error('Failed to update task');
    }
  };

  // Edit task
  const handleEditTask = (task) => {
    setEditingTask({
      ...task,
      time: task.time || '',
      description: task.description || ''
    });
    setShowEditTask(true);
  };

  const saveEditTask = async () => {
    if (!editingTask?.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          title: editingTask.title.trim(),
          description: editingTask.description.trim() || null,
          time: editingTask.time || null,
          priority: editingTask.priority
        })
        .eq('id', editingTask.id);

      if (error) throw error;

      setTasks(prev => prev.map(t => 
        t.id === editingTask.id ? { ...t, ...editingTask } : t
      ));
      setShowEditTask(false);
      setEditingTask(null);
      toast.success('Task updated!');
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error('Failed to update task');
    }
  };

  // Delete task
  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskToDelete.id);

      if (error) throw error;

      setTasks(prev => prev.filter(t => t.id !== taskToDelete.id));
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
      toast.success('Task deleted');
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error('Failed to delete task');
    }
  };

  // Add suggestion as task
  const addSuggestion = async (suggestion) => {
    try {
      const taskData = {
        user_id: user.id,
        title: suggestion,
        date: format(selectedDate, 'yyyy-MM-dd'),
        priority: 'none',
        completed: false
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert(taskData)
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [...prev, data]);
      toast.success('Task added from suggestion!');
    } catch (err) {
      console.error('Error adding suggestion:', err);
      toast.error('Failed to add task');
    }
  };

  // CMD+Enter to submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (showEditTask) {
        saveEditTask();
      } else {
        addTask();
      }
    }
  };

  const todayTasks = getTasksForToday();
  const todayEvents = getEventsForToday();
  const suggestions = getSuggestionsForToday();

  /* ------------------------------------------------------------------ Studio
     119 §21 (mock frames 9, 10, 16): the page title, then the planner-day
     composition — the tasks column with the calendar card and the card2 shared
     box, and the sticky suggestions column. Every handler, guard, list cap and
     string below is the classic one; only the shapes change. */
  const sharedTasksNode = (
    <SharedTasksSection
      tasks={sharedTasks}
      currentUserId={user?.id}
      onToggle={onToggleSharedTask}
      onDelete={onDeleteSharedTask}
      onEdit={onUpdateSharedTask ? setEditingSharedTask : undefined}
      highlightId={highlightSharedTaskId}
      setHoveredItem={setHoveredItem}
      isOwner={sharedContext?.role === 'owner'}
    />
  );

  const stTaskRow = (task) => {
    const st = subtasksApi.getForTask(task);
    const expanded = expandedSubtasks.has(task.id);
    return (
      <React.Fragment key={task.id}>
        <div className={`st-prow${task.completed ? ' st-done' : ''}`}>
          <button
            type="button"
            className={`st-cb${task.completed ? ' st-on' : ''}`}
            aria-label={task.completed ? 'Mark not done' : 'Mark done'}
            onClick={() => toggleTaskComplete(task)}
          >
            {task.completed ? <Check /> : null}
          </button>
          <span className="st-pd" style={{ background: priorityDot(task.priority) }} />
          <span className="st-txt">
            <span>{task.title}</span>
            {task.is_recurring && <Repeat className="st-rep" />}
            {st.items.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className="st-chip"
                data-testid="subtask-progress-chip"
                title={expanded ? 'Hide sub-tasks' : 'Show sub-tasks'}
                onClick={(e) => { e.stopPropagation(); toggleSubtaskExpand(task.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSubtaskExpand(task.id); } }}
              >
                <ListChecks />{st.checkedIds.length}/{st.items.length}
                {expanded ? <ChevronUp /> : <ChevronDown />}
              </span>
            )}
          </span>
          {task.isOverdue && (
            <span className="st-over">Overdue</span>
          )}
          <span className="st-time">{task.time ? formatTime12h(task.time) : ''}</span>
          <span className="st-acts">
            <button type="button" className="st-ibtn" aria-label="Edit task" onClick={() => handleEditTask(task)}><Edit /></button>
            <button type="button" className="st-ibtn" aria-label="Delete task" onClick={() => handleDeleteClick(task)}><Trash2 /></button>
          </span>
        </div>
        {expanded && st.items.length > 0 && (
          <SubtaskChecklist
            items={st.items}
            checkedIds={st.checkedIds}
            onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
          />
        )}
      </React.Fragment>
    );
  };

  const stBody = () => {
    const doneCount = todayTasks.filter(t => t.completed).length;
    return (
      <div>
        <div className="st-tktitle">
          <h1>{format(selectedDate, 'EEEE, MMMM d')}</h1>
          <span>Today</span>
        </div>
        <div className="st-tkgrid">
          <div className="st-tkcol">
            {todayEvents.length > 0 && (
              <section className="st-card">
                <div className="st-wh">
                  <h2 className="st-wt">From your calendar<small>{todayEvents.length}</small></h2>
                </div>
                <div className="st-wb">
                  <div>
                    {todayEvents.map(event => (
                      <a
                        key={event.id}
                        href={event.html_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="st-prow st-tkcal"
                      >
                        <span className="st-caldot" />
                        <span className="st-tktime">
                          {event.all_day ? 'All day' : format(new Date(event.start), 'h:mm a')}
                        </span>
                        <span className="st-txt"><span>{event.title}</span></span>
                        <span className="st-chip">Google</span>
                        <ExternalLink className="st-tkext" />
                      </a>
                    ))}
                  </div>
                </div>
              </section>
            )}

            <section className="st-card">
              <div className="st-wh">
                <h2 className="st-wt">
                  Today&rsquo;s tasks
                  {todayTasks.length > 0 && <small>{doneCount} of {todayTasks.length} done</small>}
                </h2>
                <div className="st-wr">
                  <button type="button" className="st-btn st-pri st-sm" onClick={() => setShowAddTask(true)}>
                    <Plus />Add task
                  </button>
                </div>
              </div>
              <div className="st-wb">
                {sharedTasks.length > 0 && <div className="st-tkshared">{sharedTasksNode}</div>}
                {todayTasks.length > 0 ? (
                  <div>{todayTasks.map(stTaskRow)}</div>
                ) : (
                  <div className="st-empty">
                    <span className="st-glyph"><CheckCircle2 /></span>
                    <b>No tasks for today</b>
                    <span>Add a task to get started.</span>
                    <button type="button" className="st-btn st-sec st-sm" style={{ marginTop: 10 }} onClick={() => setShowAddTask(true)}>
                      <Plus />Add task
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="st-tkside">
            {suggestions.length > 0 && (
              suggestionsCollapsed ? (
                <button type="button" className="st-btn st-sec st-full" onClick={() => setSuggestionsCollapsed(false)}>
                  <Lightbulb />Show suggestions<ChevronDown />
                </button>
              ) : (
                <section className="st-card">
                  <div className="st-wh">
                    <h2 className="st-wt">Suggested tasks</h2>
                    <div className="st-wr">
                      <button
                        type="button"
                        className="st-ibtn"
                        style={{ width: 28, height: 28 }}
                        aria-label="Hide suggestions"
                        onClick={() => setSuggestionsCollapsed(true)}
                      >
                        <ChevronUp />
                      </button>
                    </div>
                  </div>
                  <div className="st-wb">
                    <div className="st-tkhint">Tap <Plus /> to add to today&rsquo;s tasks</div>
                    <div className="st-tksugs">
                      {suggestions.map((suggestion, idx) => (
                        <button type="button" className="st-sug" key={idx} onClick={() => addSuggestion(suggestion)}>
                          <Plus /><span>{suggestion}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </section>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={isStudio ? 'st-tkpage' : 'p-4 pb-24 space-y-4'}>
      {isStudio ? (
        <>
          {stBody()}
          <SharedTaskEditDialog
            open={!!editingSharedTask}
            onOpenChange={(v) => { if (!v) setEditingSharedTask(null); }}
            task={editingSharedTask}
            members={sharedContext?.is_admin ? (sharedContext.members || []) : []}
            currentUserId={user?.id}
            onSave={onUpdateSharedTask}
          />
        </>
      ) : (
      <>
      {/* Header */}
      <div className="text-center">
        <div className="text-sm text-slate-500 dark:text-slate-400">{format(selectedDate, 'EEEE')}</div>
        <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{format(selectedDate, 'MMMM d, yyyy')}</div>
      </div>

      {/* Calendar Events Section */}
      {todayEvents.length > 0 && (
        <Card className="shadow-sm border-slate-200 dark:border-white/10">
          <CardContent className="p-3">
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Calendar Events
            </div>
            <div className="space-y-1">
              {todayEvents.map(event => (
                <a 
                  key={event.id} 
                  href={event.html_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: event.color }} />
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-14 flex-shrink-0">
                    {event.all_day ? 'All day' : format(new Date(event.start), 'h:mm a')}
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate">{event.title}</span>
                  <ExternalLink className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shared tasks bubble — team-assigned tasks in their own space */}
      <SharedTasksSection
        tasks={sharedTasks}
        currentUserId={user?.id}
        onToggle={onToggleSharedTask}
        onDelete={onDeleteSharedTask}
        onEdit={onUpdateSharedTask ? setEditingSharedTask : undefined}
        highlightId={highlightSharedTaskId}
        setHoveredItem={setHoveredItem}
        isOwner={sharedContext?.role === 'owner'}
      />
      <SharedTaskEditDialog
        open={!!editingSharedTask}
        onOpenChange={(v) => { if (!v) setEditingSharedTask(null); }}
        task={editingSharedTask}
        members={sharedContext?.is_admin ? (sharedContext.members || []) : []}
        currentUserId={user?.id}
        onSave={onUpdateSharedTask}
      />

      {/* Tasks Section */}
      <Card className="shadow-sm border-slate-200 dark:border-white/10">
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-700 dark:text-slate-200">Today's Tasks</CardTitle>
            <Button 
              size="sm" 
              className="bg-indigo-500 hover:bg-indigo-600 h-8"
              onClick={() => setShowAddTask(true)}
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {todayTasks.length > 0 ? (
            <div className="space-y-2">
              {todayTasks.map(task => {
                const st = subtasksApi.getForTask(task);
                return (
                <React.Fragment key={task.id}>
                <div
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all ${
                    task.completed 
                      ? 'bg-slate-50 dark:bg-[#1e1f22]' 
                      : task.isOverdue 
                        ? 'bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900' 
                        : 'bg-card border border-slate-200 dark:border-white/10'
                  }`}
                >
                  <div className="shrink-0" style={{ transform: 'scale(0.75)', transformOrigin: 'center' }}>
                    <PriorityCheckbox
                      priority={task.priority}
                      checked={task.completed}
                      onCheckedChange={() => toggleTaskComplete(task)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[15px] font-medium flex items-center gap-1.5 ${
                      task.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'
                    }`}>
                      <span className="truncate">{task.title}</span>
                      {task.is_recurring && (
                        <Repeat className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                      )}
                      {st.items.length > 0 && (
                        <SubtaskProgressChip
                          done={st.checkedIds.length}
                          total={st.items.length}
                          expanded={expandedSubtasks.has(task.id)}
                          onClick={() => toggleSubtaskExpand(task.id)}
                        />
                      )}
                      {task.isOverdue && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800 dark:text-amber-200 font-medium flex-shrink-0">
                          Overdue
                        </span>
                      )}
                    </div>
                    {task.time && (
                      <div className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {formatTime12h(task.time)}
                      </div>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7"
                    onClick={() => handleEditTask(task)}
                  >
                    <Edit className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-7 w-7"
                    onClick={() => handleDeleteClick(task)}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                  </Button>
                </div>
                {expandedSubtasks.has(task.id) && st.items.length > 0 && (
                  <SubtaskChecklist
                    items={st.items}
                    checkedIds={st.checkedIds}
                    onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
                  />
                )}
                </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-indigo-50 dark:from-indigo-950/40 to-purple-50 flex items-center justify-center border-2 border-dashed border-indigo-200 dark:border-indigo-900">
                <Plus className="w-6 h-6 text-indigo-400" />
              </div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">No tasks for today</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">Add a task to get started</p>
              <Button 
                size="sm" 
                className="bg-indigo-500 hover:bg-indigo-600"
                onClick={() => setShowAddTask(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Add Task
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggested Tasks Section */}
      {suggestions.length > 0 && (
        <div>
          {suggestionsCollapsed ? (
            <button
              onClick={() => setSuggestionsCollapsed(false)}
              className="w-full p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <Lightbulb className="w-5 h-5" />
              <span className="text-sm font-medium">Show Suggestions</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          ) : (
            <Card className="shadow-md border-0 bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
              <CardHeader className="pb-2 px-4 pt-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    Suggested Tasks
                  </CardTitle>
                  <button
                    onClick={() => setSuggestionsCollapsed(true)}
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                </div>
                <CardDescription className="text-indigo-100 text-xs">
                  Tap <Plus className="w-3 h-3 inline" /> to add to today's tasks
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => addSuggestion(suggestion)}
                      className="flex items-center gap-2 w-full text-left p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4 flex-shrink-0" />
                      <span className="line-clamp-2 text-xs">{suggestion}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      </>
      )}

      {/* Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0" data-ui-dialog={isStudio ? 'task' : undefined}>
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-lg">{isStudio ? 'Add task' : 'Add Task'}</DialogTitle>
            <DialogDescription className="text-sm">
              {format(selectedDate, 'EEEE, MMMM d')}
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-4 space-y-3">
            <div>
              <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Title</Label>
              <div className="relative">
                {mentionQuery !== null && assignableMembers.length > 0 && (
                  <MentionSuggestions
                    ref={mentionSuggestionsRef}
                    query={mentionQuery}
                    members={assignableMembers}
                    groups={[]}
                    onSelect={(item) => {
                      if (item.kind !== 'user') return;
                      const el = titleInputRef.current;
                      const caret = el?.selectionStart ?? newTask.title.length;
                      const upto = newTask.title.slice(0, caret);
                      const stripped = upto.replace(/(^|\s)@\w*$/, '$1');
                      const cleanTitle = (stripped + newTask.title.slice(caret)).replace(/\s+$/, ' ');
                      setNewTask({ ...newTask, title: cleanTitle, assigned_to: item.user_id, assigned_to_name: item.user_name });
                      setMentionQuery(null);
                      setTimeout(() => el?.focus(), 0);
                    }}
                    currentUserId={user?.id}
                  />
                )}
                <Input
                  ref={titleInputRef}
                  placeholder={assignableMembers.length > 0 ? 'What needs to be done? (@ to assign)' : 'What needs to be done?'}
                  value={newTask.title}
                  onChange={(e) => {
                    setNewTask({ ...newTask, title: e.target.value });
                    if (assignableMembers.length > 0) {
                      const upto = e.target.value.slice(0, e.target.selectionStart ?? e.target.value.length);
                      const m = upto.match(/(?:^|\s)@(\w*)$/);
                      setMentionQuery(m ? m[1] : null);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (mentionQuery !== null && mentionSuggestionsRef.current?.handleKeyDown(e)) return;
                    if (e.key === 'Escape' && mentionQuery !== null) { setMentionQuery(null); e.stopPropagation(); return; }
                    handleKeyDown(e);
                  }}
                  className="h-10"
                  autoFocus
                  data-testid="mobile-add-task-title"
                />
              </div>
            </div>
            <div>
              <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Description</Label>
              <Textarea
                placeholder="Add details (optional)"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                onKeyDown={handleKeyDown}
                className="min-h-[60px] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Time</Label>
                <Select value={newTask.time || ''} onValueChange={(v) => setNewTask({ ...newTask, time: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="No time">
                      {newTask.time ? formatTime12h(newTask.time) : 'No time'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">No time</SelectItem>
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Share / assign to a teammate (admins only) */}
            {assignableMembers.length > 0 && (
              <div>
                <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Share / assign to</Label>
                {newTask.assigned_to ? (
                  isStudio ? (
                    <div className="st-tkassign" data-testid="mobile-assignee-chip">
                      <span className="st-tkpill"><span>{newTask.assigned_to_name}</span></span>
                      <span className="st-sp" />
                      <button
                        type="button"
                        className="st-ibtn st-ibtn-sm"
                        aria-label="Clear assignee"
                        onClick={() => setNewTask({ ...newTask, assigned_to: null, assigned_to_name: '' })}
                        data-testid="mobile-clear-assignee"
                      >
                        <XIcon />
                      </button>
                    </div>
                  ) : (
                  <div className="flex items-center justify-between h-10 px-3 rounded-md border border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10" data-testid="mobile-assignee-chip">
                    <span className="text-sm text-violet-700 dark:text-violet-300 truncate">{newTask.assigned_to_name}</span>
                    <button onClick={() => setNewTask({ ...newTask, assigned_to: null, assigned_to_name: '' })} className="text-violet-400 hover:text-violet-600 text-lg leading-none" data-testid="mobile-clear-assignee">×</button>
                  </div>
                  )
                ) : (
                  <Select value="" onValueChange={(uid) => {
                    const m = assignableMembers.find(x => x.user_id === uid);
                    if (m) setNewTask({ ...newTask, assigned_to: m.user_id, assigned_to_name: m.user_name });
                  }}>
                    <SelectTrigger className="h-10" data-testid="mobile-assignee-trigger">
                      <SelectValue placeholder="Assign" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {assignableMembers.map(m => (
                        <SelectItem key={m.user_id} value={m.user_id}>
                          {m.user_name}{m.user_id === user?.id ? ' (me)' : ''}{['owner', 'admin'].includes(m.role) ? ' · admin' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="px-5 pb-5" data-ui="dlg-f">
            {isStudio && (
              <span className="st-tkkbd" aria-hidden="true"><kbd>&#8984;</kbd><kbd>Enter</kbd></span>
            )}
            <Button variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
            <Button onClick={addTask} className="bg-indigo-500 hover:bg-indigo-600" data-testid="mobile-add-task-submit">
              {isStudio
                ? (newTask.assigned_to ? 'Assign task' : 'Add task')
                : (newTask.assigned_to ? 'Assign Task' : 'Add Task')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditTask} onOpenChange={setShowEditTask}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0" data-ui-dialog={isStudio ? 'task' : undefined}>
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-lg">{isStudio ? 'Edit task' : 'Edit Task'}</DialogTitle>
            {isStudio && (
              <DialogDescription className="text-sm">{format(selectedDate, 'EEEE, MMMM d')}</DialogDescription>
            )}
          </DialogHeader>
          <div className="px-5 pb-4 space-y-3">
            <div>
              <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Title</Label>
              <Input
                placeholder="What needs to be done?"
                value={editingTask?.title || ''}
                onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                onKeyDown={handleKeyDown}
                className="h-10"
              />
            </div>
            <div>
              <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Description</Label>
              <Textarea
                placeholder="Add details (optional)"
                value={editingTask?.description || ''}
                onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                onKeyDown={handleKeyDown}
                className="min-h-[60px] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Time</Label>
                <Select value={editingTask?.time || ''} onValueChange={(v) => setEditingTask({ ...editingTask, time: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="No time">
                      {editingTask?.time ? formatTime12h(editingTask.time) : 'No time'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="none">No time</SelectItem>
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={isStudio ? 'st-fldlbl' : "text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block"}>Priority</Label>
                <Select 
                  value={editingTask?.priority || 'none'} 
                  onValueChange={(v) => setEditingTask({ ...editingTask, priority: v })}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="px-5 pb-5" data-ui="dlg-f">
            {isStudio && (
              <span className="st-tkkbd" aria-hidden="true"><kbd>&#8984;</kbd><kbd>Enter</kbd></span>
            )}
            <Button variant="outline" onClick={() => setShowEditTask(false)}>Cancel</Button>
            <Button onClick={saveEditTask} className="bg-indigo-500 hover:bg-indigo-600">{isStudio ? 'Save changes' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className={isStudio ? 'sm:max-w-sm' : 'sm:max-w-[350px]'} data-ui-dialog={isStudio ? 'confirm' : undefined}>
          <DialogHeader>
            <DialogTitle>{isStudio ? `Delete “${taskToDelete?.title || ''}”?` : 'Delete Task?'}</DialogTitle>
            <DialogDescription>
              {isStudio
                ? 'This can\u2019t be undone.'
                : `Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter data-ui="dlg-f">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MobileTasksView;
